import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { FileTextIcon, UploadIcon } from "lucide-react";

import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";

import { FileChooser } from "components/lib/Choosers";

const DOCX_ACCEPT_TYPES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
].join(",");

type ImportWordModalProps = {
  open: boolean;
  onClose: () => void;
  ideaId: string;
  onImported: (response: any) => void;
};

const ImportWordModal = ({
  open,
  onClose,
  ideaId,
  onImported,
}: ImportWordModalProps) => {
  const { t } = useTranslation();
  const [uploadedFile, setUploadedFile] = useState<{
    savedName: string;
    fileName: string;
  } | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const resetStateAndClose = useCallback(() => {
    setUploadedFile(null);
    setIsImporting(false);
    onClose?.();
  }, [onClose]);

  const importFromWord = useCallback(() => {
    if (!uploadedFile?.savedName) {
      return;
    }

    setIsImporting(true);
    api.ideas.importFromWord(
      ideaId,
      { fileName: uploadedFile.savedName },
      (response) => {
        setIsImporting(false);
        if (!response?.success) {
          toast.error(response?.message || "No new content was imported");
          return;
        }

        toast.success(
          t("ideas.importWord.success", {
            count: response.importedFieldCount || 0,
          }),
        );
        onImported?.(response);
        resetStateAndClose();
      },
      (err) => {
        setIsImporting(false);
        toast.error(err.message || "Unable to import Word content");
      },
    );
  }, [uploadedFile, ideaId, onImported, resetStateAndClose, t]);

  const canImport = useMemo(
    () => !!uploadedFile?.savedName && !isImporting,
    [uploadedFile, isImporting],
  );

  return (
    <Modal open={open} onClose={resetStateAndClose} size="small">
      <ModalHeader>
        {t("ideas.importWord.title")}{" "}
        <Label size="small" color="violet">
          {t("generic.beta")}
        </Label>
      </ModalHeader>
      <ModalContent>
        <p style={{ marginTop: 0 }}>{t("ideas.importWord.intro")}</p>
        <ol style={{ paddingLeft: 18, margin: 0 }}>
          <li style={{ marginBottom: 18 }}>
            <strong>{t("ideas.importWord.stepOne.title")}</strong>
            <p style={{ marginTop: 8 }}>{t("ideas.importWord.stepOne.info")}</p>
          </li>
          <li style={{ marginBottom: 18 }}>
            <strong>{t("ideas.importWord.stepTwo.title")}</strong>
            <p style={{ marginTop: 8 }}>{t("ideas.importWord.stepTwo.info")}</p>
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong>{t("ideas.importWord.stepThree.title")}</strong>
            <p style={{ marginTop: 8 }}>
              {t("ideas.importWord.stepThree.info")}
            </p>
            <FileChooser
              forType="idea"
              forId={ideaId}
              maxMBFileSize={100}
              accept={DOCX_ACCEPT_TYPES}
              trigger={
                <Button>
                  <Icon icon={UploadIcon} />
                  {t("ideas.importWord.selectFile")}
                </Button>
              }
              onComplete={(savedName, fileName) => {
                setUploadedFile({ savedName, fileName });
              }}
            />
          </li>
        </ol>
        {uploadedFile ? (
          <Message color="green" style={{ marginTop: 12 }}>
            <Icon icon={FileTextIcon} />
            <MessageHeader>{t("ideas.importWord.fileReady")}</MessageHeader>
            <MessageContent>{uploadedFile.fileName}</MessageContent>
          </Message>
        ) : null}

        <Message color="orange" size="small" style={{ marginTop: 12 }}>
          {t("ideas.importWord.caveats")}
        </Message>
      </ModalContent>
      <ModalActions>
        <Button onClick={resetStateAndClose}>{t("generic.cancel")}</Button>
        <Button
          loading={isImporting}
          disabled={!canImport}
          onClick={importFromWord}
          color="primary"
        >
          {t("ideas.importWord.confirm")}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default ImportWordModal;
